import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Typography,
  TableRow,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import PipelineService, { pipelineErrorMessage } from "../../services/pipeline.service";
import { IRuleCacheStats } from "../../types/api/pipeline.type";
import useNotifications from "../../components/NotificationProvider";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";

/** Stats are a flat-ish dict whose keys vary with cache state; nested values are shown as compact JSON. */
const formatValue = (value: unknown) => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const RuleCacheTab = ({ envId }: { envId: string }) => {
  const { setNotification } = useNotifications();
  const [stats, setStats] = useState<IRuleCacheStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmInvalidate, setConfirmInvalidate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await PipelineService.getRuleCacheStats(envId);
      setStats(data);
      setError(null);
    } catch (e) {
      setStats(null);
      setError(pipelineErrorMessage(e, "Could not load rule cache stats"));
    } finally {
      setLoading(false);
    }
  }, [envId]);

  useEffect(() => {
    load();
  }, [load]);

  const invalidate = async () => {
    setConfirmInvalidate(false);
    try {
      const { data } = await PipelineService.invalidateRuleCache(envId);
      const dropped = (data as { dropped?: unknown })?.dropped;
      setNotification({
        message: `Rule cache invalidated${dropped !== undefined ? ` (${formatValue(dropped)} dropped)` : ""}`,
        open: true,
      });
    } catch (e) {
      setNotification({ message: pipelineErrorMessage(e), open: true, type: "error" });
    } finally {
      await load();
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          The pipeline caches alert rules in memory. alert-manager invalidates it when rules change; invalidate
          by hand if a rule change isn't being picked up.
        </Typography>
        <Button variant="contained" onClick={() => setConfirmInvalidate(true)}>
          Invalidate cache
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        stats && (
          <TableContainer component={Paper} sx={{ maxWidth: 800 }}>
            <Table size="small" aria-label="rule cache stats">
              <TableBody>
                {Object.entries(stats).map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell sx={{ fontWeight: "bold", width: 240 }}>{key.replace(/_/g, " ")}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>{formatValue(value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      <ConfirmationDialog
        open={confirmInvalidate}
        setOpen={setConfirmInvalidate}
        title="Invalidate rule cache"
        content="Drops every cached alert rule; the pipeline reloads them from the database on the next lookup."
        onProceed={invalidate}
      />
    </Box>
  );
};

export default RuleCacheTab;
